import React, { useRef, useState, useEffect } from "react";
import {
  CloudUpload,
  Description,
  CheckCircle,
  ErrorOutline,
  WarningAmber,
  DeleteOutline,
  Download,
  Visibility,
  ArrowForward,
  AutoAwesome,
  Inventory2,
  Close,
  Refresh,
  FileDownload,
} from "@mui/icons-material";

import "./imports.css";

import {useSelector} from "react-redux";
import axios from "axios";
import * as XLSX from "xlsx";
import {saveAs} from "file-saver";
import {serverHost} from "../../../../http_common.js";
import {TransactionRecordPreview} from "./previews/PreviewSales.jsx";
import {purchaseAmount,purchaseDate} from "./exports/OrdersPDFExport.jsx";
export const importColumns=['sku','name','quantity','costPrice','sellingPrice','currency','barcode','itemType','category','brand','unit','taxRate','description'];
const normalizedHeaders=Object.fromEntries(importColumns.map(name=>[name.replace(/[^a-z0-9]/gi,'').toLowerCase(),name]));
export function validateImportRows(raw,existing=[]){if(!raw.length||raw.length>1000)throw new Error('Select a file with between 1 and 1000 item rows.');const barcodes=new Set(existing.map(item=>item.barcode).filter(Boolean));return raw.map((row,index)=>{const item=Object.fromEntries(Object.entries(row).map(([key,value])=>[normalizedHeaders[key.replace(/[^a-z0-9]/gi,'').toLowerCase()]||key,typeof value==='string'?value.trim():value]));const errors=[];item.sku=String(item.sku||'').trim();item.name=String(item.name||'').trim();if(!item.sku||!item.name)errors.push('SKU prefix and name are required.');for(const field of ['quantity','costPrice','sellingPrice']){if(item[field]===undefined||item[field]===''||!Number.isFinite(Number(item[field]))||Number(item[field])<0)errors.push(`${field} must be a non-negative number.`);else item[field]=Number(item[field]);}item.taxRate=item.taxRate===undefined||item.taxRate===''?0:Number(item.taxRate);if(!Number.isFinite(item.taxRate)||item.taxRate<0||item.taxRate>100)errors.push('taxRate must be from 0 to 100.');item.currency=String(item.currency||'NGN').toUpperCase();if(!/^[A-Z]{3}$/.test(item.currency))errors.push('Currency must have three letters.');item.itemType=item.itemType||'Product';if(!['Product','Production','Service'].includes(item.itemType))errors.push('Use Product, Production or Service itemType.');item.barcode=String(item.barcode||'');if(item.barcode){if(barcodes.has(item.barcode))errors.push('Barcode already exists in this file or inventory.');barcodes.add(item.barcode);}return {...item,row:index+1,price:purchaseAmount(item.sellingPrice,item.currency),status:errors.length?'error':'valid',errors,validationErrors:errors};});}
export default function Imports(){
 const company=useSelector(state=>state.clientFunction?.companiesDB?.clientData),inputRef=useRef(null),jobId=useRef(''),pending=useRef(false),fileSerial=useRef(0);
 const [file,setFile]=useState(null),[isDragging,setIsDragging]=useState(false),[uploadProgress,setUploadProgress]=useState(0),[isUploading,setIsUploading]=useState(false),[activeStep,setActiveStep]=useState(1),[previewItems,setPreview]=useState([]),[recentUploads,setHistory]=useState([]),[inventories,setInventories]=useState([]),[inventoryId,setInventory]=useState(''),[error,setError]=useState(''),[selected,setSelected]=useState(null),[job,setJob]=useState(null),[allRows,setAllRows]=useState(false),[showHistory,setShowHistory]=useState(false);
 const validCount=previewItems.filter(row=>!row.validationErrors.length).length,errorCount=previewItems.filter(row=>row.validationErrors.length||row.status==='FAILED').length;
 async function history(signal){if(!company?.clientId)return;const response=await axios.get(`${serverHost}/manage_products/client/${encodeURIComponent(company.clientId)}/imports`,{signal});if(![200,201].includes(response.data.status))throw new Error(response.data.message);if(!signal?.aborted)setHistory((response.data.importsData||[]).map(record=>({...record,date:purchaseDate(record.createdAt),records:record.rowCount})));}
 useEffect(()=>{const controller=new AbortController();if(company?.clientId){axios.get(`${serverHost}/conditionalInventoryFetch/get-inventories/${encodeURIComponent(company.clientId)}`,{signal:controller.signal}).then(response=>{if(![200,201].includes(response.data.status))throw new Error(response.data.message);if(!controller.signal.aborted)setInventories(response.data.inventoriesData||[]);}).catch(reason=>{if(!controller.signal.aborted)setError(reason.message);});history(controller.signal).catch(reason=>{if(!controller.signal.aborted)setError(reason.response?.data?.message||reason.message);});}return()=>controller.abort();},[company?.clientId]);
 async function handleFile(selectedFile){if(!selectedFile||pending.current)return;const serial=++fileSerial.current;setFile(selectedFile);setPreview([]);setJob(null);jobId.current=`IMPORT-${window.crypto.randomUUID()}`;setError('');setIsUploading(true);setUploadProgress(0);setActiveStep(2);try{if(selectedFile.size>25*1024*1024||!/[.](csv|xlsx|xls)$/i.test(selectedFile.name))throw new Error('Select a CSV, XLSX or XLS file below 25 MB.');const bytes=await selectedFile.arrayBuffer(),book=XLSX.read(bytes,{type:'array'}),raw=XLSX.utils.sheet_to_json(book.Sheets[book.SheetNames[0]],{defval:''});const rows=validateImportRows(raw);if(serial!==fileSerial.current)return;setPreview(rows);setUploadProgress(100);setActiveStep(3);}catch(reason){if(serial===fileSerial.current)setError(reason.message);}finally{if(serial===fileSerial.current)setIsUploading(false);}}
 const handleInputChange=event=>handleFile(event.target.files?.[0]),handleDrop=event=>{event.preventDefault();setIsDragging(false);handleFile(event.dataTransfer.files?.[0]);},removeFile=()=>{if(pending.current)return;++fileSerial.current;setFile(null);setPreview([]);setJob(null);setUploadProgress(0);setActiveStep(1);if(inputRef.current)inputRef.current.value='';};
 function downloadTemplate(){const book=XLSX.utils.book_new();XLSX.utils.book_append_sheet(book,XLSX.utils.aoa_to_sheet([importColumns]),'Items');saveAs(new Blob([XLSX.write(book,{type:'array',bookType:'xlsx'})]),'Items_Import_Template.xlsx');}
 async function importFile(){if(pending.current||!company?.clientId||!inventoryId||!previewItems.length||previewItems.some(row=>row.validationErrors.length)||job?.status==='COMPLETED')return;pending.current=true;setIsUploading(true);setError('');try{const items=previewItems.map(row=>Object.fromEntries(importColumns.filter(key=>row[key]!==undefined).map(key=>[key,row[key]]))),response=await axios.post(`${serverHost}/client/${encodeURIComponent(company.clientId)}/manage_products/post_import`,{importId:jobId.current,clientId:company.clientId,inventoryId,name:file.name,items});if(![200,201,207].includes(response.data.status))throw new Error(response.data.message);const saved=response.data.savedRecord||response.data.info;setJob(saved);setSelected(saved);setPreview(rows=>rows.map(row=>{const result=saved.results?.find(result=>result.row===row.row);return result?{...row,displaySku:result.sku,status:result.status,errors:result.status==='FAILED'?[result.message]:[]}:row;}));await history();}catch(reason){setError(reason.response?.data?.message||reason.message);}finally{pending.current=false;setIsUploading(false);}}
  return (
    <section className="item-upload-page">
      {showHistory&&<div className="client_modal_overlay fx-jc fx-ac" onClick={()=>setShowHistory(false)}><div className="client_modal" role="dialog" aria-label="Import history" onClick={event=>event.stopPropagation()}><h3>Saved import history</h3><button className="controlButtons" onClick={()=>setShowHistory(false)}>Close</button>{recentUploads.map(record=><button className="item-upload-recent__item" key={record.importId} onClick={()=>{setShowHistory(false);setSelected(record);}}>{record.name} | {record.importId} | {record.status} | {record.importedCount} imported / {record.failedCount} failed</button>)}</div></div>}
      {error&&<p role="alert">{error}</p>}
      {selected&&<div className="client_modal_overlay fx-jc fx-ac" onClick={()=>setSelected(null)}><div className="client_modal" onClick={event=>event.stopPropagation()}><TransactionRecordPreview record={selected} kind="import" onClose={()=>setSelected(null)}/></div></div>}

      {/* HEADER */}
      <div className="item-upload-header">

        <div className="item-upload-header__left">
          <div className="item-upload-header__icon">
            <Inventory2 />
          </div>

          <div>
            <div className="item-upload-header__eyebrow">
              INVENTORY MANAGEMENT
            </div>

            <h1>Import Items</h1>

            <p>
              Quickly add products, stock levels and item information
              into your Universe Inventory workspace.
            </p>
          </div>
        </div>

        <button className="item-upload-template-button" onClick={downloadTemplate}>
          <Download />
          Download Template
        </button>

      </div>


      {/* STEPPER */}
      <label>Receiving Inventory<select aria-label="Receiving Inventory" value={inventoryId} disabled={pending.current} onChange={event=>setInventory(event.target.value)}><option value="">Select saved inventory</option>{inventories.map(inv=><option key={inv.inventoryId} value={inv.inventoryId}>{inv.name||inv.inventoryId}</option>)}</select></label>
      <div className="item-upload-stepper">

        <div className="item-upload-stepper__line">
          <span
            className={activeStep >= 2 ? "is-active" : ""}
          />
        </div>

        <div className={`item-upload-step ${activeStep >= 1 ? "is-active" : ""}`}>
          <span>01</span>
          <div>
            <strong>Select file</strong>
            <small>Choose your inventory file</small>
          </div>
        </div>

        <div className={`item-upload-step ${activeStep >= 2 ? "is-active" : ""}`}>
          <span>02</span>
          <div>
            <strong>Validate</strong>
            <small>Check your inventory data</small>
          </div>
        </div>

        <div className={`item-upload-step ${activeStep >= 3 ? "is-active" : ""}`}>
          <span>03</span>
          <div>
            <strong>Import</strong>
            <small>Add items to inventory</small>
          </div>
        </div>

      </div>


      {/* MAIN GRID */}
      <div className="item-upload-layout">

        <div className="item-upload-main">

          {/* UPLOAD CARD */}
          <div className="item-upload-card">

            <div className="item-upload-card__top">
              <div>
                <span className="item-upload-card__label">
                  ITEM DATA
                </span>

                <h2>Upload inventory file</h2>

                <p>
                  Import hundreds or thousands of items in seconds.
                </p>
              </div>

              <div className="item-upload-ai">
                <AutoAwesome />
                Smart Import
              </div>
            </div>


            {!file ? (
              <div
                className={`item-upload-dropzone ${
                  isDragging ? "is-dragging" : ""
                }`}
                onDragOver={(event) => {
                  event.preventDefault();
                  setIsDragging(true);
                }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={handleDrop}
                onClick={() => inputRef.current?.click()}
              >

                <input
                  ref={inputRef}
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  hidden
                  onChange={handleInputChange}
                />

                <div className="item-upload-dropzone__icon">
                  <CloudUpload />
                </div>

                <h3>
                  Drop your inventory file here
                </h3>

                <p>
                  or <span>browse from your computer</span>
                </p>

                <div className="item-upload-supported">
                  <span>CSV</span>
                  <span>XLSX</span>
                  <span>XLS</span>
                  <small>
                    Maximum file size: 25 MB
                  </small>
                </div>

              </div>
            ) : (
              <div className="item-upload-file">

                <div className="item-upload-file__icon">
                  <Description />
                </div>

                <div className="item-upload-file__details">
                  <strong>{file.name}</strong>

                  <span>
                    {(file.size / 1024 / 1024).toFixed(2)} MB
                  </span>

                  {isUploading && (
                    <div className="item-upload-progress">

                      <div className="item-upload-progress__track">
                        <span
                          style={{
                            width: `${uploadProgress}%`,
                          }}
                        />
                      </div>

                      <small>
                        Processing file... {uploadProgress}%
                      </small>

                    </div>
                  )}

                  {!isUploading && (
                    <div className="item-upload-file__success">
                      <CheckCircle />
                      {job?.status==='COMPLETED'?'Import completed':errorCount?'Rows require attention':'File parsed and validated'}
                    </div>
                  )}

                </div>

                <button
                  className="item-upload-file__remove"
                  onClick={removeFile}
                >
                  <Close />
                </button>

              </div>
            )}

          </div>


          {/* VALIDATION SUMMARY */}
          {file && !isUploading && (
            <div className="item-upload-validation">

              <div className="item-upload-validation__header">

                <div>
                  <span className="item-upload-card__label">
                    DATA VALIDATION
                  </span>

                  <h2>Import preview</h2>

                  <p>
                    Review your records before adding them to inventory.
                  </p>
                </div>

                <button className="item-upload-refresh" disabled={isUploading||!previewItems.length} onClick={()=>{try{setPreview(validateImportRows(previewItems));setJob(null);setError('');}catch(reason){setError(reason.message);}}}>
                  <Refresh />
                  Revalidate
                </button>

              </div>


              <div className="item-upload-stats">

                <div className="item-upload-stat">
                  <span>Total records</span>
                  <strong>{previewItems.length}</strong>
                  <small>Rows detected</small>
                </div>

                <div className="item-upload-stat item-upload-stat--success">
                  <span>Valid</span>
                  <strong>{validCount}</strong>
                  <small>Ready to import</small>
                </div>

                <div className="item-upload-stat item-upload-stat--warning">
                  <span>Warnings</span>
                  <strong>{job?.failedCount||0}</strong>
                  <small>Review recommended</small>
                </div>

                <div className="item-upload-stat item-upload-stat--error">
                  <span>Errors</span>
                  <strong>{errorCount}</strong>
                  <small>Require attention</small>
                </div>

              </div>


              {/* TABLE */}
              <div className="item-upload-table-wrapper">

                <div className="item-upload-table-toolbar">

                  <strong>Inventory records</strong>

                  <button onClick={()=>setAllRows(value=>!value)}>
                    <Visibility />
                    {allRows?"Show first 10 rows":"View all records"}
                  </button>

                </div>

                <table className="item-upload-table">

                  <thead>
                    <tr>
                      <th>SKU</th>
                      <th>ITEM NAME</th>
                      <th>CATEGORY</th>
                      <th>QUANTITY</th>
                      <th>PRICE</th>
                      <th>STATUS</th>
                    </tr>
                  </thead>

                  <tbody>
                    {(allRows?previewItems:previewItems.slice(0,10)).map((item) => (
                      <tr key={item.row}>

                        <td>
                          <strong>{item.displaySku||item.sku}</strong>
                        </td>

                        <td>{item.name}</td>

                        <td>
                          <span className="item-upload-category">
                            {item.category}
                          </span>
                        </td>

                        <td>{item.quantity}</td>

                        <td>{item.price}</td>

                        <td>
                          {["valid","IMPORTED"].includes(item.status) && (
                            <span className="item-upload-status item-upload-status--success">
                              <CheckCircle />
                              Valid
                            </span>
                          )}

                          {item.status === "warning" && (
                            <span className="item-upload-status item-upload-status--warning">
                              <WarningAmber />
                              Warning
                            </span>
                          )}

                          {["error","FAILED"].includes(item.status) && (
                            <span className="item-upload-status item-upload-status--error">
                              <ErrorOutline />
                              Error: {item.errors.join(" ")}
                            </span>
                          )}
                        </td>

                      </tr>
                    ))}
                  </tbody>

                </table>

              </div>


              {/* ACTION */}
              <div className="item-upload-action">

                <div className="item-upload-action__message">
                  <CheckCircle />

                  <div>
                    <strong>Ready for import</strong>
                    <span>
                      {validCount} validated rows are ready for the selected inventory.
                    </span>
                  </div>
                </div>

                <button className="item-upload-import-button" onClick={importFile} disabled={isUploading||!inventoryId||!previewItems.length||previewItems.some(row=>row.validationErrors.length)||job?.status==='COMPLETED'}>
                  Import {validCount} Items
                  <ArrowForward />
                </button>

              </div>

            </div>
          )}

        </div>


        {/* SIDEBAR */}
        <aside className="item-upload-sidebar">

          {/* IMPORT GUIDE */}
          <div className="item-upload-guide">

            <div className="item-upload-guide__icon">
              <AutoAwesome />
            </div>

            <span className="item-upload-card__label">
              SMART IMPORT
            </span>

            <h3>
              Import without the headache.
            </h3>

            <p>
              Universe automatically detects your columns,
              validates item data and highlights records that
              need your attention.
            </p>

            <div className="item-upload-guide__features">

              <div>
                <CheckCircle />
                <span>Automatic column detection</span>
              </div>

              <div>
                <CheckCircle />
                <span>Duplicate item detection</span>
              </div>

              <div>
                <CheckCircle />
                <span>SKU validation</span>
              </div>

              <div>
                <CheckCircle />
                <span>Price & quantity validation</span>
              </div>

            </div>

          </div>


          {/* MAPPING */}
          <div className="item-upload-mapping">

            <div className="item-upload-mapping__header">
              <div>
                <span className="item-upload-card__label">
                  COLUMN MAPPING
                </span>

                <h3>Required fields</h3>
              </div>

              <FileDownload />
            </div>

            <div className="item-upload-mapping__list">

              <div>
                <span>Item name</span>
                <CheckCircle />
              </div>

              <div>
                <span>SKU</span>
                <CheckCircle />
              </div>

              <div>
                <span>Quantity</span>
                <CheckCircle />
              </div>

              <div>
                <span>Selling price</span>
                <CheckCircle />
              </div>

              <div>
                <span>Category</span>
                <small>Optional</small>
              </div>

            </div>

          </div>


          {/* RECENT */}
          <div className="item-upload-recent">

            <div className="item-upload-recent__header">
              <h3>Recent imports</h3>

              <button onClick={()=>setShowHistory(true)}>View all</button>
            </div>

            {recentUploads.map((upload) => (
              <div
                className="item-upload-recent__item"
                onClick={()=>setSelected(upload)} role="button" tabIndex={0} onKeyDown={event=>{if(event.key==='Enter')setSelected(upload);}}
                key={upload.importId}
              >

                <div className="item-upload-recent__file">
                  <Description />
                </div>

                <div>
                  <strong>{upload.name}</strong>

                  <span>
                    {upload.records} records · {upload.date} · {upload.status}
                  </span>
                </div>

                <CheckCircle />

              </div>
            ))}

          </div>

        </aside>

      </div>

    </section>
  );
}
 